import { createContext, useContext, useEffect, useState, useRef } from "react";
import type { User } from "@supabase/supabase-js";
import {
  getCurrentUser,
  onAuthStateChange,
  signIn as supabaseSignIn,
  signOut,
  signUp,
  updateProfile,
  uploadAvatar,
  supabase,
} from "../lib/supabase";
import { organizationService } from "../services/organizationService";
import { userService } from "../services/userService";
import type { Organization } from "../types/database";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  currentOrganization: Organization | null;
  userOrganizations: any[];
  pendingInvitations: any[];
  signIn: (
    email: string,
    password: string
  ) => Promise<{
    data: any;
    error: any;
  }>;
  signUp: (
    email: string,
    password: string
  ) => Promise<{
    data: any;
    error: any;
  }>;
  signOut: () => Promise<{
    error: any;
  }>;
  updateUserProfile: (updates: {
    full_name?: string;
    avatar_url?: string;
  }) => Promise<{
    data: any;
    error: any;
  }>;
  uploadUserAvatar: (file: File) => Promise<{
    data: any;
    error: any;
  }>;
  refreshUser: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<boolean>;
  createOrganization: (organization: {
    name: string;
    logo_url?: string | null;
    primary_color?: string | null;
    secondary_color?: string | null;
  }) => Promise<Organization>;
  acceptInvitation: (invitationId: string) => Promise<boolean>;
  declineInvitation: (invitationId: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentOrganization, setCurrentOrganization] =
    useState<Organization | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<any[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Add cache to prevent unnecessary reloads
  const lastRefreshTimestamp = useRef<number>(0);
  const refreshCooldown = 2000; // 2 seconds cooldown between refreshes
  const isRefreshing = useRef<boolean>(false);

  const refreshUser = async () => {
    // Skip if already refreshing or if we refreshed recently
    const now = Date.now();
    if (
      isRefreshing.current ||
      now - lastRefreshTimestamp.current < refreshCooldown
    ) {
      console.log(
        "AuthContext - Skipping refresh (too recent or already in progress)"
      );
      return;
    }

    console.log("AuthContext - refreshUser called");
    isRefreshing.current = true;
    setLoading(true);

    try {
      const { user } = await getCurrentUser();
      console.log("AuthContext - refreshUser got user:", user?.id);
      console.log(
        "AuthContext - refreshUser user metadata:",
        user?.user_metadata
      );
      setUser(user);

      if (user) {
        console.log("Loading organizations for user:", user.id);
        await loadUserOrganizations(user.id);
        await loadPendingInvitations(user.id);

        // Add a small delay to ensure all state updates are complete
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      lastRefreshTimestamp.current = Date.now();
    } catch (error) {
      console.error("Error refreshing user:", error);
    } finally {
      setLoading(false);
      isRefreshing.current = false;
    }
  };

  const loadUserOrganizations = async (userId: string) => {
    try {
      console.log("Loading user organizations for user:", userId);

      // Get all organizations the user is a member of
      const orgs = await organizationService.getUserOrganizations(userId);
      console.log("Raw organizations from service:", orgs);

      setUserOrganizations(orgs);
      console.log("User organizations found:", orgs.length);

      // If user has no organizations, return early
      if (orgs.length === 0) {
        console.log("User has no organizations");
        setCurrentOrganization(null);
        return false;
      }

      // Debug: Log the first organization structure
      if (orgs.length > 0) {
        console.log("First organization structure:", orgs[0]);
        console.log("First organization ID:", orgs[0].organization_id);
      }

      // Get the user's profile to check current organization
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("current_organization_id")
        .eq("id", userId)
        .single();

      console.log("Profile data:", profile);
      console.log("Profile error:", profileError);

      let currentOrgId = profile?.current_organization_id;

      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        // If profile doesn't exist, create it
        if (profileError.code === "PGRST116") {
          console.log("Creating new user profile");
          const { error: insertError } = await supabase
            .from("profiles")
            .insert({ id: userId, current_organization_id: null });

          if (insertError) {
            console.error("Error creating user profile:", insertError);
          }
        }
      }

      // If no current organization is set but user has organizations, set the first one
      if (!currentOrgId && orgs.length > 0) {
        currentOrgId = orgs[0].organization_id;
        console.log("Setting first organization as current:", currentOrgId);

        const { error: updateError } = await supabase.from("profiles").upsert({
          id: userId,
          current_organization_id: currentOrgId,
          updated_at: new Date().toISOString(),
        });

        if (updateError) {
          console.error("Error updating current organization:", updateError);
        } else {
          console.log("Successfully updated current organization in profile");
        }
      }

      // Verify that the current organization ID is still valid (user is still a member)
      if (currentOrgId) {
        const isStillMember = orgs.some(
          (org) => org.organization_id === currentOrgId
        );
        if (!isStillMember) {
          console.log("Current organization is no longer valid, resetting");
          currentOrgId = null;

          // Clear the invalid current organization
          await supabase
            .from("profiles")
            .update({ current_organization_id: null })
            .eq("id", userId);
        }
      }

      // If still no current organization but user has organizations, set the first one
      if (!currentOrgId && orgs.length > 0) {
        currentOrgId = orgs[0].organization_id;
        console.log(
          "Setting first organization as current (after validation):",
          currentOrgId
        );

        const { error: updateError } = await supabase.from("profiles").upsert({
          id: userId,
          current_organization_id: currentOrgId,
          updated_at: new Date().toISOString(),
        });

        if (updateError) {
          console.error("Error updating current organization:", updateError);
        } else {
          console.log(
            "Successfully updated current organization in profile (validation)"
          );
        }
      }

      // Load the current organization details
      if (currentOrgId) {
        console.log("Loading organization details for:", currentOrgId);
        const org = await organizationService.getOrganization(currentOrgId);
        console.log("Organization details loaded:", org);
        setCurrentOrganization(org);
        console.log("Current organization set to:", org?.name);
        return true; // Successfully set current organization
      } else {
        setCurrentOrganization(null);
        console.log("No current organization set");
        return false; // No current organization set
      }
    } catch (error) {
      console.error("Error loading user organizations:", error);
      setUserOrganizations([]);
      setCurrentOrganization(null);
      return false;
    }
  };

  const loadPendingInvitations = async (userId: string) => {
    try {
      const invitations = await userService.getPendingInvitations(userId);
      setPendingInvitations(invitations);
    } catch (error) {
      console.error("Error loading pending invitations:", error);
      setPendingInvitations([]);
    }
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    // Check for current user
    getCurrentUser().then(({ user }) => {
      if (!isMounted) return;

      setUser(user);

      if (user) {
        loadUserOrganizations(user.id)
          .then(() => {
            if (isMounted) {
              return loadPendingInvitations(user.id);
            }
          })
          .finally(() => {
            if (isMounted) {
              setLoading(false);
              setInitialLoadComplete(true);
              lastRefreshTimestamp.current = Date.now();
            }
          });
      } else {
        if (isMounted) {
          setLoading(false);
          setInitialLoadComplete(true);
        }
      }
    });

    // Subscribe to auth changes
    const {
      data: { subscription },
    } = onAuthStateChange((event, session) => {
      if (!isMounted) return;

      console.log("Auth state changed:", event, session?.user?.id);
      setUser(session?.user ?? null);

      // If a new session is created, refresh the user data to get the profile
      if (event === "SIGNED_IN" && session?.user) {
        setLoading(true);
        setTimeout(() => {
          if (isMounted) {
            refreshUser().catch((err) => {
              console.error("Error refreshing user after auth change:", err);
              if (isMounted) {
                setLoading(false);
              }
            });
          }
        }, 500); // Small delay to ensure auth is complete
      } else if (event === "SIGNED_OUT") {
        // Clear organization data on sign out
        setCurrentOrganization(null);
        setUserOrganizations([]);
        setPendingInvitations([]);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Enhanced sign in function that refreshes user data after login
  const signIn = async (email: string, password: string) => {
    setLoading(true);
    const result = await supabaseSignIn(email, password);

    if (!result.error && result.data?.user) {
      console.log("Sign in successful, refreshing user data...");

      // Add a small delay to ensure auth is complete before fetching profile
      await new Promise((resolve) => setTimeout(resolve, 500));
      await refreshUser();

      // Add additional delay to ensure organization is properly set
      await new Promise((resolve) => setTimeout(resolve, 200));

      console.log(
        "Sign in complete, current organization:",
        currentOrganization?.name
      );

      // Ensure loading is set to false after refreshUser completes
      setLoading(false);
    } else {
      setLoading(false);
    }

    return result;
  };

  // Profile management functions
  const updateUserProfile = async (updates: {
    full_name?: string;
    avatar_url?: string;
  }) => {
    if (!user) {
      return { data: null, error: new Error("User not authenticated") };
    }

    // Set default full_name if not provided to bypass onboarding
    const updatesWithDefaults = {
      ...updates,
      full_name:
        updates.full_name ||
        user.user_metadata?.full_name ||
        user.email?.split("@")[0] ||
        "User",
    };

    const result = await updateProfile(user.id, updatesWithDefaults);

    // Refresh user data to get updated profile
    if (!result.error) {
      await refreshUser();
    }

    return result;
  };

  const uploadUserAvatar = async (file: File) => {
    if (!user) {
      return { data: null, error: new Error("User not authenticated") };
    }

    const result = await uploadAvatar(user.id, file);

    // Ensure we always return the expected structure
    if ("error" in result && result.error) {
      return { data: null, error: result.error };
    }

    // Refresh user data to get updated avatar
    await refreshUser();

    return { data: result.data, error: null };
  };

  // Organization management functions
  const switchOrganization = async (organizationId: string) => {
    if (!user) {
      console.error("Cannot switch organization: User not authenticated");
      return false;
    }

    try {
      setLoading(true);
      // Update the user's current organization in the database
      const success = await userService.updateCurrentOrganization(
        user.id,
        organizationId
      );

      if (success) {
        // Load the new organization details
        const org = await organizationService.getOrganization(organizationId);
        setCurrentOrganization(org);
        setLoading(false);
        return true;
      }
      setLoading(false);
      return false;
    } catch (error) {
      console.error("Error switching organization:", error);
      setLoading(false);
      return false;
    }
  };

  const createOrganization = async (organization: {
    name: string;
    logo_url?: string | null;
    primary_color?: string | null;
    secondary_color?: string | null;
  }) => {
    if (!user) {
      throw new Error("User not authenticated");
    }

    try {
      setLoading(true);
      // Make sure we pass the correct types to the service
      const orgData = {
        name: organization.name,
        logo_url: organization.logo_url || null,
        primary_color: organization.primary_color || null,
        secondary_color: organization.secondary_color || null,
      };

      const newOrg = await organizationService.createOrganization(
        orgData,
        user.id
      );

      // Refresh the user's organizations
      await loadUserOrganizations(user.id);
      setLoading(false);
      return newOrg;
    } catch (error) {
      console.error("Error creating organization:", error);
      setLoading(false);
      throw error;
    }
  };

  const acceptInvitation = async (invitationId: string) => {
    if (!user) {
      console.error("Cannot accept invitation: User not authenticated");
      return false;
    }

    try {
      setLoading(true);
      const success = await userService.acceptInvitation(invitationId, user.id);
      if (success) {
        await refreshUser();
        return true;
      }
      setLoading(false);
      return false;
    } catch (error) {
      console.error("Error accepting invitation:", error);
      setLoading(false);
      return false;
    }
  };

  const declineInvitation = async (invitationId: string) => {
    if (!user) {
      console.error("Cannot decline invitation: User not authenticated");
      return false;
    }

    try {
      setLoading(true);
      const success = await userService.declineInvitation(
        invitationId,
        user.id
      );
      if (success) {
        await refreshUser();
        return true;
      }
      setLoading(false);
      return false;
    } catch (error) {
      console.error("Error declining invitation:", error);
      setLoading(false);
      return false;
    }
  };

  // Custom signOut that preserves current organization
  const customSignOut = async () => {
    // Don't clear current_organization_id on sign out
    // Just sign out from Supabase auth
    const result = await signOut();

    // Clear local state
    setUser(null);
    setCurrentOrganization(null);
    setUserOrganizations([]);
    setPendingInvitations([]);

    return result;
  };

  const value: AuthContextType = {
    user,
    loading,
    currentOrganization,
    userOrganizations,
    pendingInvitations,
    signIn,
    signUp,
    signOut: customSignOut,
    updateUserProfile,
    uploadUserAvatar,
    refreshUser,
    switchOrganization,
    createOrganization,
    acceptInvitation,
    declineInvitation,
  };

  return (
    <AuthContext.Provider value={value}>
      {initialLoadComplete ? children : <div>Loading application...</div>}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
